/*
 * Copyright (c) 2023 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useContext, useEffect } from "react";
import { Container, Label, Input, Button, Tooltip, Card, CardBody } from "reactstrap";
import { AccountContext, ContextUpdater } from "../../context/ContextProvider";
import SortableTable from "../../elements/SortableTable";
import useUpdateState from "../../util/useUpdateState";
import GuestProfile from "../guest/GuestProfile";
import { requestClientUsers } from "../../context/RequestManager";
import CenterModal from "../../elements/CenterModal";
import SplitField from "../../provisioning/elements/SplitField";

const TableLabel = ({faded = false, children}) => {
  const noData = children == null || children === "";
  return (
    <td className={`${faded || noData ? "text-muted fw-light" : ""} label-ellipsis`} style={{ maxWidth: "160px" }}>
      <span>{noData ? "-" : children}</span>
    </td>
  );
};

const TableIndicator = ({children}) => {
  let color;
  switch (children) {
    case "Online": color = "#2C2"; break;
    case "Offline": color = "#C22"; break;
    case "Unknown": color = "#888"; break;
    default: return null;
  }

  return (
    <td
      className={`${children === "Unknown" ? "text-muted fw-light" : ""} label-ellipsis d-flex`}
      style={{ maxWidth: "160px" }}
    >
      <div style={{ width: "8px", height: "8px", borderRadius: "5px", backgroundColor: color, margin: "6px 4px 0 0" }} />
      <span>{children}</span>
    </td>
  );
}

export const UserListSize = 50;
export const UserListInitialSize = 1 /*pages*/ * UserListSize /*per page*/;

const UserSearch = () => {
  // constants
  const downloadButtonId = "user-search-download-button";
  const searchCategories = [
    { id: "all", key: null, name: "All" },
    { id: "name", key: "name", name: "Name" },
    { id: "email", key: "email", name: "Email" },
  ];

  const initialSortKey = "lastLogin";

  // context
  const updateContexts = useContext(ContextUpdater);
  const accountContext = useContext(AccountContext);

  const initialState = {
    search: null,
    category: searchCategories[0].id,
    csvTooltip: false,
    selectedUser: null,
    location: "",
    loading: false,
    page: 1,
    displayLocationList: null,
  };
  const [state, , updateState] = useUpdateState(initialState);

  // const [currentPage, setCurrentPage] = useState(1);

  // Constants
  const gdprRejectedText = "Removed due to GDPR";

  // prepare account list
  const locationList = accountContext.userLocationList;
  const locationInfo = locationList?.reduce((acc, item) => {
    acc[item.id] = item.name;
    return acc;
  }, {})

  const sortLocation = (itemA, itemB) => {
    // Needs to match sort tables sort method to match as enums
    let a = itemA?.length ? itemA.toLowerCase() : null;
    let b = itemB?.length ? itemB.toLowerCase() : null;
    let ret = 0;
    if (a == null && b != null) return -1;
    else if (a != null && b == null) return -1;
    else if (a > b) ret = 1;
    else if (a < b) ret = -1;
    return ret ;
  }

  const guestProfile = accountContext.guestProfile;
  const users = accountContext.userList?.map((item) => {
    let user = item;
    user.isOnlineStatus = user.isOnlineStatus != null && user.isOnlineStatus !== "unknown" ? user.isOnlineStatus : "offline";
    user.locations = user.locationList?.map((item) => { return locationInfo?.[item] ?? null });
    if (user.gdprAccepted === 0) { 
      // Update name, email, phone number, etc
      user.name = user.name ?? gdprRejectedText;
      user.email = user.email ?? gdprRejectedText;
      user.phoneNumber = user.phoneNumber ?? gdprRejectedText;
      user.gender = user.gender ?? gdprRejectedText;
    }
    
    return user;
  });

  useEffect(() => {
    // Update the userlist with the guest profile
    if (guestProfile != null) {
      accountContext.updateState({
        userList: accountContext.userList.map(item => {
          if (item.id === guestProfile?.id) {
            const filteredGuestProfile = Object.fromEntries(
              Object.entries(guestProfile).filter(([key, value]) => value != null)
            )

            const merged = { 
              ...item, 
              ...filteredGuestProfile,
            };

            return merged;
          } else {
            return item;
          }
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestProfile]);

  useEffect(() => {
    if (!accountContext.clientId) {
      return;
    }
    
    updateState({ loading: true });
    requestClientUsers({
      context: updateContexts,
      token: accountContext.token,
      clientId: accountContext.clientId,
      showLoading: false,
      completion: () => {
        updateState({ loading: false });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredUsers = state.location ? users.filter(user => {
    return user.locationList?.some(location => location === state.location);
  }): users;

  if (users == null) {
    return (
      <Container className="d-flex flex-column align-items-center justify-content-center">
        <Label className="m-5" tag="h3">Error loading guests</Label>
      </Container>
    );
  }

  // styles
  const smoothTransitionStyle = {
    overflow: "hidden",
    transition: "all 0.6s ease-in-out"
  };

  // table columns
  const renderTableLabel = ({ index, data, id, value }) => {
    switch (id) {
      case "isOnlineStatus": return <TableIndicator key={index}>{value}</TableIndicator>;
      default: 
        const isApple = id === "email" && data.type === "apple";
        const gdprRejected = value === gdprRejectedText;
        return <TableLabel key={index} faded={isApple || gdprRejected}>{value}</TableLabel>;
    }
  };

  const renderTableLocationList = ({ index, data, id, value }) => {
    if (!value) {
      return null;
    }

    const sorted = value?.sort((a, b) => sortLocation(a, b));
    const locationName = sorted?.[0] ?? "-";
    const hasMore = (sorted?.length ?? 0) > 1;

    const handleClick = (event) => {
      event.stopPropagation();
      updateState({displayLocationList: sorted })
    }

    return (
      <TableLabel key={index}>
        {locationName}
        {' '}
        {hasMore && <span style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }} onClick={(event) => handleClick(event)} >...</span>}
      </TableLabel>
    )
  };

  const loginTypeEnum = {
    free_wifi: "Free WiFi",
    passcode: "Passcode",
    email: "Email",
    sms: "SMS",
    google: "Google",
    facebook: "Facebook",
    // apple: "Apple",
    // linkedin: "LinkedIn",
    twitter: "Twitter"
  };

  const locationEnum = () => {
    const userLocations = filteredUsers?.map((i) => i.locations?.sort((itemA, itemB) => { return sortLocation(itemA, itemB); })).filter(i => i.length) ?? [];
    return Object.fromEntries(userLocations.map(i => [i.join(), i]));
  }

  const loginEnum = { loyal: "Loyal", new: "New", returning: "Returning" };
  const statusEnum = { online: "Online", offline: "Offline" };
  const columns = [
    { id: "name", name: "Name", type: "string", excludeSort: gdprRejectedText, render: renderTableLabel },
    { id: "email", name: "Email", type: "string", excludeSort: gdprRejectedText, render: renderTableLabel },
    { id: "locations", name: "Location", type: "list", enum: locationEnum(), unknownEnum: !state.location, render: renderTableLocationList },
    { id: "type", name: "Login Type", type: "enum", enum: loginTypeEnum, render: renderTableLabel },
    { id: "visits", name: "Visits", type: "number", render: renderTableLabel },
    { id: "loyaltyStatus", name: "Loyalty", type: "enum", enum: loginEnum, render: renderTableLabel },
    { id: "lastLogin", name: "Last Login", type: "time", render: renderTableLabel },
    { id: "isOnlineStatus", name: "Status", type: "enum", enum: statusEnum, unknownEnum: true, render: renderTableLabel }
  ];

  const gdprAcceptedEnum = {
    0: "Denied",
    1: "Accepted",
  }

  const downloadKeys = [
    { id: "name", name: "Name", type: "string" },
    { id: "email", name: "Email", type: "string" },
    { id: "phoneNumber", name: "Phone Number", type: "string"},
    { id: "type", name: "Login Type", type: "enum", enum: loginTypeEnum },
    { id: "age", name: "Age", type: "number" },
    { id: "birthday", name: "Birthday", type: "string"},
    { id: "marketingEmailOptIn", name: "Email Opt In", type: "boolean"},
    { id: "marketingSmsOptIn", name: "SMS Opt In", type: "boolean" },
    { id: "gdprAccepted", name: "GDPR Accepted", type: "enum", enum: gdprAcceptedEnum },
    { id: "language", name: "Language", type: "string"},
    { id: "userLanguage", name: "Browser Language", type: "string" },
    { id: "locale", name: "Locale", type: "string"},
    { id: "country", name: "Country", type: "string"},
    { id: "timezone", name: "Timezone", type: "string"},
    { id: "visits", name: "Visits", type: "number" },
    { id: "loyaltyStatus", name: "Loyalty", type: "enum", enum: loginEnum },
    { id: "lastLogin", name: "Last Login", type: "time" },
    { id: "isOnlineStatus", name: "Status", type: "enum", enum: statusEnum, unknownEnum: true },
    { id: "customTextField", name: "Custom Text", type: "string" },
    { id: "locations", name: "Location", type: "list" }
  ]

  // table search filter
  const filterColumn = searchCategories.find(category => category.id === state.category)?.key;
  const filter = {
    string: state.search,
    columns: filterColumn ? [ filterColumn ] : null,
    filterKeys: ["customTextField"]
  };

  return (
    <div className="mx-4 pt-3 contentArea d-lg-flex flex-column">
      <div className="d-flex justify-content-between">
        <div style={{ width: "300px" }} />
        <Label className="w-100 fw-bold text-center" tag="h1">Search Guests</Label>
        <div style={{ width: "300px" }}>
          <Label className="no-spacing" size="sm">Location</Label>
          <Input
            className="mb-3"
            type="select"
            value={state.location}
            disabled={state.loading}
            onChange={(e) => updateState({ location: e.target.value, page: 1})}
          >
            <option value="">All Locations</option>
            {locationList.map((location, index) => (
              <option key={index} value={location.id}>{location.name}</option>
            ))}
          </Input>
        </div>
      </div>
      <Card className="flex-static">
        <CardBody className="mb-1 d-flex">
          <div className="flex-dynamic p-1">
            <Label className="fw-bold" tag="h5">Search for anything</Label>
            <Input bsSize="lg" disabled={state.loading} placeholder="Search" onChange={(event) => updateState({ search: event.target.value, page: 1 })} />
          </div>
          <div className="flex-static p-1" style={{ minWidth: "25%" }}>
            <Label className="fw-bold" tag="h5">Category</Label>
            <Input bsSize="lg" type="select" disabled={state.loading} onChange={(event) => updateState({ category: event.target.value, page: 1 })}>
              {searchCategories.map((category, index) => (
                <option key={index} value={category.id}>{category.name}</option>
              ))}
            </Input>
          </div>
          <div className="flex-static p-1 d-flex align-items-end">
            <Button id={downloadButtonId} disabled={state.loading} outline>
              <i className="icon bi bi-cloud-download" style={{ fontSize: "1.3rem" }}/>
            </Button>
            <Tooltip
              placement="top"
              isOpen={state.csvTooltip}
              target={downloadButtonId}
              toggle={() => updateState({ csvTooltip: !state.csvTooltip })}
            >
              Export as CSV
            </Tooltip>
          </div>
        </CardBody>
      </Card>
      <div className="flex-dynamic d-flex">
        <Card className={`flex-${state.selectedUser ? "60" : "100"} d-flex`} style={smoothTransitionStyle}>
          <SortableTable
            data={filteredUsers}
            columns={columns}
            filter={filter}
            filterEnabled={!state.loading}
            initialSortKey={initialSortKey}
            placeholder="No guests found"
            downloadButtonId={downloadButtonId}
            downloadKeys={downloadKeys}
            selectValue={state.selectedUser?.id}
            paging={true}
            currentPage={state.page}
            onPageChanged={(p) => updateState({ page: p })}
            onSelected={(userId) => {
              const newUser = state.selectedUser?.id === userId ? null : users.find(user => user.id === userId);
              updateState({ selectedUser: newUser });
            }}
          />
        </Card>
        <GuestProfile
          guestId={state.selectedUser?.id}
          onDismiss={() => updateState({ selectedUser: null })}
        />
        <CenterModal isOpen={state?.displayLocationList}  toggle={() => updateState({ displayLocationList: null })}>
          <div>
            <Label className="w-100 text-muted text-center" tag="h4">Guest Locations</Label>
            <Label className="m-2" tag="h5">Location</Label> 
            {Object.values(state.displayLocationList ?? []).map((item) => {
              return (
                <SplitField>

                  <Label className="m-2">{item}</Label>
                </SplitField>
              )
            })}
            <Button onClick={() => updateState({ displayLocationList: null })}>Close</Button>
          </div>
        </CenterModal> 
      </div>
    </div>
  );
};

export default UserSearch;
